import React, { useState, useEffect, useMemo } from 'react';
import { useIntl } from 'umi';
import { Justify } from 'tea-component';
import { scrollable } from 'tea-component/lib/table/addons';

import RenderOperations from '@sg/components/Chart/Operations';
import Chart from '@sg/components/Chart';
import TableContent from '@sg/components/TableContent';
import Loading from '@intelligence/components/Loading';

import { pickOperations } from '@sg/commonData/pickOperations';
import { xAxisHighLight, applyValueAxisBoundaryGap } from '@sg/utils/chatUtils';
import { financeFormat } from '@sg/utils/common';
import { appendStackedTotalLabelSeries } from '@/pages/strategic/utils/stackedTotalLabel';
import { createBtnPoint, SGBuriedBtnPoint } from '@/pages/strategic/utils/buriedPoint';

import {
  DefaultFinancialsChat,
  getStudioCardChartHeight,
  getStudioCardTableBodyMaxHeight,
  showFinancialsList,
} from '../../Studios/config';
import { getBurnRateColumns } from './config/columns';
import { useBurnRateData } from './hooks/useBurnRateData';
import { applyWrappedXAxisLabels } from '../utils/applyTruncatedXAxisLabels';

import './index.scss';

type TViewStateKey = 'financials' | 'burnrate' | 'headcount' | 'staffrate';

/** Standalone Burn Rate card for Portfolio Overview (own title + ops). */
interface IBurnRateCardProps {
  activeDateTypeSegment: string;
  selectedMonth?: string | null;
  smallChartViewState: Record<string, boolean>;
  updateStudioEyesViewState: (viewKey: TViewStateKey, enabled: boolean) => void;
  buriedPointTab: any;
  studioInfo?: string;
}

const BurnRateCard: React.FC<IBurnRateCardProps> = ({
  activeDateTypeSegment,
  selectedMonth,
  smallChartViewState,
  updateStudioEyesViewState,
  buriedPointTab,
  studioInfo = '',
}) => {
  const { formatMessage } = useIntl();
  const [showBurnRateChatsType, setShowBurnRateChatsType] = useState<any>(DefaultFinancialsChat);
  const viewEnabled = !!smallChartViewState.burnrate;

  useEffect(() => {
    setShowBurnRateChatsType((prev: any) => ({
      ...prev,
      format: viewEnabled,
    }));
  }, [viewEnabled]);

  const {
    burnRateData,
    burnRateTableData,
    burnRateUpdateTime,
    burnRateLoading,
  } = useBurnRateData(
    { scope: 'portfolio' },
    activeDateTypeSegment,
    selectedMonth,
  );

  const burnRateColumns = useMemo(
    () => getBurnRateColumns('portfolio'),
    [],
  );

  const chartHeight = useMemo(() => getStudioCardChartHeight(), []);
  const tableBodyMaxHeight = useMemo(
    () => getStudioCardTableBodyMaxHeight({ tableHeaderRows: 1 }),
    [],
  );

  const handleShowBurnRateChatsTypeChange = (e: any) => {
    if (e?.value) {
      const value = e.value;
      const isViewClick = value === 'view'
        || (e.format !== showBurnRateChatsType?.format
          && value === showBurnRateChatsType?.value);

      if (isViewClick) {
        const nextViewEnabled = !viewEnabled;
        const btnPoint = createBtnPoint(
          buriedPointTab,
          'burnRateView',
          `Burn Rate - View - ${nextViewEnabled ? 'On' : 'Off'} - ${studioInfo}`,
        );
        if (btnPoint) {
          SGBuriedBtnPoint(btnPoint);
        }
        updateStudioEyesViewState('burnrate', nextViewEnabled);
        setShowBurnRateChatsType((prev: any) => ({
          ...prev,
          format: nextViewEnabled,
        }));
        return;
      }

      if (value === 'bar' && showBurnRateChatsType?.value !== 'bar') {
        const btnPoint = createBtnPoint(
          buriedPointTab,
          'burnRateBarChart',
          `Burn Rate - Bar Chart - ${studioInfo}`,
        );
        if (btnPoint) {
          SGBuriedBtnPoint(btnPoint);
        }
      } else if (value === 'table' && showBurnRateChatsType?.value !== 'table') {
        const btnPoint = createBtnPoint(
          buriedPointTab,
          'burnRateTable',
          `Burn Rate - Table - ${studioInfo}`,
        );
        if (btnPoint) {
          SGBuriedBtnPoint(btnPoint);
        }
      }
    }

    setShowBurnRateChatsType({
      ...e,
      format: viewEnabled,
    });
  };

  return (
    <div className="dbs-by-date-card dbs-burn-rate-card">
      <Justify
        bottom
        left={
          <div className="dbo-card-title dbs-by-date-card-title">
            <div className="dbs-burn-rate-card__title">
              {`${formatMessage({ id: 'dbs_burn_rate' })} ($)`}
            </div>
            <div className="dbo-card-title-sub">
              {burnRateUpdateTime
                ? `${formatMessage({ id: 'dbs_update_on' })} ${burnRateUpdateTime}`
                : ''}
            </div>
          </div>
        }
        right={
          <RenderOperations
            key={`burnrate-ops-${showBurnRateChatsType.value}-${viewEnabled}`}
            defaultValue={showBurnRateChatsType.value}
            defaultValueShow={showBurnRateChatsType.format}
            change={handleShowBurnRateChatsTypeChange}
            options={pickOperations(['view', ...showFinancialsList], formatMessage)}
            downloadParams={{
              data: burnRateTableData,
              columns: burnRateColumns,
              name: formatMessage({ id: 'dbs_burn_rate' }),
            }}
          />
        }
      />

      {burnRateLoading && (
        <Loading style={{ height: chartHeight }} />
      )}

      {!burnRateLoading
        && showBurnRateChatsType?.value !== 'table' && (
        <Chart
          data={burnRateData[showBurnRateChatsType?.value] || {
            xAxisData: [],
            type: 'bar',
            seriesData: [],
          }}
          key={`burnRate${showBurnRateChatsType?.value}`}
          type={showBurnRateChatsType?.value}
          height={chartHeight}
          formatType="bmk"
          setOtherConfigFunc={(opt: any) => {
            opt.grid.containLabel = true;
            if (showBurnRateChatsType?.value === 'bar') {
              // Pad y-axis so bar/total labels are not clipped at the chart edge.
              applyValueAxisBoundaryGap(opt);
            }
            opt = xAxisHighLight(opt, {
              type: showBurnRateChatsType?.value === 'bar' ? 'shadow' : 'line',
              lineStyle: {
                color: '#BBBDCB',
                width: 1,
                type: 'solid',
              },
            } as any);
            // After xAxisHighLight: truncate ticks only; hover keeps full name.
            applyWrappedXAxisLabels(opt);

            if (Array.isArray(opt?.series)) {
              opt.series = opt.series.map((series: any) => {
                if (series?.type === 'line') {
                  return { ...series, z: 2, zlevel: 0 };
                }
                if (series?.type === 'bar') {
                  return { ...series, z: 1, zlevel: 0 };
                }
                return series;
              });
            }

            opt = appendStackedTotalLabelSeries(opt, {
              enabled: showBurnRateChatsType?.value === 'bar' && viewEnabled,
              seriesName: '__total_label__',
              labelFormatter: (value: number) => financeFormat(value, 1),
            });

            return opt;
          }}
        />
      )}

      {!burnRateLoading
        && showBurnRateChatsType?.value === 'table' && (
        <TableContent
          className="dbs-commons-table dbs-chats-table"
          emptyMinHeight={chartHeight}
          datas={burnRateTableData}
          columns={burnRateColumns}
          sort={[{ by: 'fill_time', order: 'desc' }]}
          resultKey="list"
          recordKey="key"
          addons={[scrollable({
            minWidth: 580,
            maxHeight: tableBodyMaxHeight,
          })]}
        />
      )}
    </div>
  );
};

export default BurnRateCard;
